import { Injectable } from '@angular/core';
import { ItineraryType, SegmentType } from '../../enums/enums';
import { Itinerary, Passenger, Segment } from '../../models/itinerary.model';
import { ExchangeAvailabilityOption, ExchangeSegment } from '../../models/voluntary-change.model';
import { MiscUtil } from 'src/app/search-result/utils/misc.util';
import { DOCSADoc } from '../../models/docsa.model';

@Injectable({
  providedIn: 'root'
})
export class MapItineraryApi {
  mapExchangeOption(option: ExchangeAvailabilityOption): ExchangeAvailabilityOption {
    const type = this.itineraryType(option.segments || []);
    return {
      ...option,
      type,
      segments: this.segmentsType(option.segments, type),
      originalExchangeFare: option.exchangeFare,
      exchangeFare: option.exchangeFare
    };
  }

  mapItinerary(itinerary: Itinerary): Itinerary {
    const type = this.itineraryType(itinerary.segments || []);
    return {
      ...itinerary,
      type,
      segments: this.segmentsType(itinerary.segments, type)
    };
  }

  mapExchangeQuoteRequest(exchangeSegments: ExchangeSegment[]): ExchangeSegment[] {
    return exchangeSegments.map((segment, index) => ({
      ...segment,
      originalIndex: index + 1,
      id: MiscUtil.uuid()
    }));
  }

  mapSegments(segments: Segment[]): Segment[] {
    return segments?.map((segment) => {
      segment.flights = segment?.flights.map((flight) => {
        const { urlIcon, ...restFlight } = flight;
        return restFlight;
      });
      const { iconsUrl, ...restSegment } = segment;
      return restSegment;
    });
  }

  private segmentsType(segments: Segment[], type: ItineraryType): Segment[] {
    return segments.map((segment, index) => {
      if ((type === ItineraryType.RoundTrip && index === 0) || type === ItineraryType.OneWay) {
        segment.type = SegmentType.Departure;
      } else if (type === ItineraryType.RoundTrip && index === 1) {
        segment.type = SegmentType.Return;
      } else {
        segment.type = SegmentType.Route;
      }
      return segment;
    });
  }

  private itineraryType(segments: Segment[]): ItineraryType {
    if (segments.length === 2 && this.isRoundTrip(segments)) {
      return ItineraryType.RoundTrip;
    } else if (segments.length === 1) {
      return ItineraryType.OneWay;
    }
    return ItineraryType.Multidestiny;
  }

  private isRoundTrip(segments: Segment[]): boolean {
    return segments[0].departureLocation.code === segments[1].arrivalLocation.code;
  }

  createExchangeOption(segments: Segment[]): ExchangeAvailabilityOption {
    const type = this.itineraryType(segments || []);
    return {
      type,
      segments: this.segmentsType(segments, type),
      exchangeFare: null
    } as ExchangeAvailabilityOption;
  }

  needAditionalInformation(passengers: Passenger[]) {
    let needAditionalInformation = false;
    let noExists = 0;
    let oneHaveEmail = passengers.filter((x) => x.email).length > noExists;
    let oneHavePhone = passengers.filter((x) => x.phoneNumber).length > noExists;
    let dontHaveBirthDate =
      passengers.filter((x) => x.birthDate === '0001-01-01T00:00:00' || x.birthDate === null)
        .length > noExists;
    let dontHaveGender = passengers.filter((x) => x.gender === null).length > noExists;

    if (!oneHaveEmail || !oneHavePhone || dontHaveBirthDate || dontHaveGender) {
      needAditionalInformation = true;
    }
    return needAditionalInformation;
  }

  docsasValid(docs: DOCSADoc[]) {
    let allHaveBirthdateAndGender = docs.filter(
      (res) =>
        res.pasengerDateOfBirth === null ||
        res.pasengerDateOfBirth === '0001-01-01T00:00:00' ||
        res.gender === null ||
        res.gender === ''
    );

    let oneContainsEmail = docs.filter((res) => res.email === null || res.email === '');
    let oneContainsPhoneNumber = docs.filter(
      (res) => res.phoneNumber === '' || res.phoneNumber === null
    );

    return (
      allHaveBirthdateAndGender.length === 0 && // all passengers must have gender and birthDate
      docs.length > oneContainsEmail.length && // at least passenger have email
      docs.length > oneContainsPhoneNumber.length // at least passenger have phoneNumber
    );
  }
}
